import { useCallback } from 'react';
import noop from 'lodash/noop';

import useScreen from '@lyearn/core/hooks/useScreen';
import { CloseOutline } from '@lyearn/icons';
import { IconButton } from '@lyearn/ui';

import DialogBody from '../Primitives/DialogBody';
import { DialogBodyProps } from '../Primitives/DialogBody/types';
import DialogRoot from '../Primitives/DialogRoot';
import { AlertDialogProps } from './types';

const getAlertDialogOnClose = (onClose: AlertDialogProps['onClose']) => onClose || noop;

const desktopStyles: DialogBodyProps['styles'] = {
  header: {
    className: 'px-24 pt-28',
    overrides: { description: { className: 'mt-8 px-8', variant: 'body-short-02' } },
  },
  footer: {
    buttonSize: 'medium',
    className: 'mt-20 px-24 py-20',
  },
};
const mobileStyles: DialogBodyProps['styles'] = {
  header: {
    className: 'px-16 pt-36',
    overrides: { title: { variant: 'heading-s' }, description: { className: 'mt-12 px-8' } },
  },
  footer: {
    variant: 'fullWidth',
    gapX: 12,
    buttonSize: 'large',
    className: 'mt-20 p-16 pb-36',
  },
};

export const AlertDialogBody = (props: Omit<AlertDialogProps, 'open'>) => {
  const { isMobile } = useScreen();
  const { onClose: onCloseProp, styles, ...rest } = props;

  return (
    <DialogBody
      {...rest}
      styles={styles || (isMobile ? mobileStyles : desktopStyles)}
      onClose={getAlertDialogOnClose(onCloseProp)}
    />
  );
};

export const AlertDialogRoot = (
  props: Pick<AlertDialogProps, 'open' | 'onClose' | 'children' | 'width'>,
) => {
  const { open, onClose: onCloseProp, width = 416, children } = props;
  const onClose = getAlertDialogOnClose(onCloseProp);

  const renderCloseButton = useCallback(() => {
    if (!onCloseProp) {
      return null;
    }

    return (
      <IconButton
        Icon={CloseOutline}
        appearance="secondary"
        className="absolute top-16 end-16"
        shape="square"
        size="s"
        variant="ghost"
        onClick={onCloseProp}
      />
    );
  }, [onCloseProp]);

  return (
    <DialogRoot open={open} renderCloseButton={renderCloseButton} width={width} onClose={onClose}>
      {children}
    </DialogRoot>
  );
};

const AlertDialog = (props: AlertDialogProps) => {
  const { open, onClose, width, ...rest } = props;

  return (
    <AlertDialogRoot open={open} width={width} onClose={onClose}>
      <AlertDialogBody {...rest} onClose={onClose} />
    </AlertDialogRoot>
  );
};

export default AlertDialog;
